import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "./lib/auth";
import { ConfirmProvider } from "./lib/confirm";
import { isAdminHost } from "./lib/host";
import InstallPrompt from "./components/InstallPrompt";
import FrozenScreen from "./components/FrozenScreen";
import LoginPage from "./pages/LoginPage";
import FirstLoginPage from "./pages/FirstLoginPage";
import AuditLogPage from "./pages/AuditLogPage";
import DonationsPage from "./pages/DonationsPage";
import LibraryPage from "./pages/LibraryPage";
import PlansePage from "./pages/PlansePage";
import VotesPage from "./pages/VotesPage";
import EducationPage from "./pages/EducationPage";
import CertificatesPage from "./pages/CertificatesPage";
import MeetingMinutesPage from "./pages/MeetingMinutesPage";
import DiplomasPage from "./pages/DiplomasPage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import TransfersPage from "./pages/TransfersPage";
import SuspensionsPage from "./pages/SuspensionsPage";
import AppShell from "./components/AppShell";
import DashboardPage from "./pages/DashboardPage";
import MembersPage from "./pages/MembersPage";
import MemberProfilePage from "./pages/MemberProfilePage";
import EventsPage from "./pages/EventsPage";
import DocumentsPage from "./pages/DocumentsPage";
import RecognizedLodgesPage from "./pages/RecognizedLodgesPage";
import LodgeInfoPage from "./pages/LodgeInfoPage";
import SupportPage from "./pages/SupportPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ConsentPage from "./pages/ConsentPage";
import PetitionPublicPage from "./pages/PetitionPublicPage";
import MeetWebJoinPage from "./pages/MeetWebJoinPage";
import MailPage from "./pages/MailPage";
import { MeetingSessionProvider } from "./lib/meetingSession";
import PetitionsPage from "./pages/PetitionsPage";
import ChildSafetyPage from "./pages/ChildSafetyPage";
import SafetyReportsPage from "./pages/SafetyReportsPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import UsersPage from "./pages/UsersPage";
import DuesPage from "./pages/DuesPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import AttendancePage from "./pages/AttendancePage";
import CheckInPage from "./pages/CheckInPage";
import PassportNumbersPage from "./pages/PassportNumbersPage";
import PassportsBrowserPage from "./pages/PassportsBrowserPage";
import GlobalMembersPage from "./pages/GlobalMembersPage";
import LodgeChatPage from "./pages/LodgeChatPage";
import CheckInScanPage from "./pages/CheckInScanPage";
import JurisdictionLodgeOverviewPage from "./pages/JurisdictionLodgeOverviewPage";
import MobileAppPage from "./pages/MobileAppPage";
import OnlineMeetingsPage from "./pages/OnlineMeetingsPage";

const ADMIN_HOST = isAdminHost();

function Protected({ children, adminOnly = false, superAdminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="auth-bg flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;

  if (superAdminOnly && user.role !== "superadmin") return <Navigate to="/" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AdminHostRoutes() {
  // Reserved for a future dedicated admin.* deployment. Currently behaves like
  // MainHostRoutes so superadmins can still operate on `admin.*` without losing
  // access to the wider feature surface.
  return <MainHostRoutes />;
}

function MainHostRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/first-login" element={<FirstLoginPage />} />
      {/* Public unauthenticated route — required by App Store / Google Play
          submissions and referenced from mobile app onboarding. */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/consent/:token" element={<ConsentPage />} />
      <Route path="/petition/:token" element={<PetitionPublicPage />} />
      <Route path="/meet" element={<MeetWebJoinPage />} />
      <Route path="/meet/:code" element={<MeetWebJoinPage />} />
      <Route path="/child-safety" element={<ChildSafetyPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      {/* Super-Admin route is available on lodge.* / preview / apex too, so superadmins can still access it. */}
      <Route path="/superadmin" element={<Protected superAdminOnly><SuperAdminPage /></Protected>} />
      <Route path="/superadmin/passport-numbers" element={<Protected superAdminOnly><PassportNumbersPage /></Protected>} />
      <Route path="/superadmin/suspensions" element={<Protected superAdminOnly><SuspensionsPage /></Protected>} />
      <Route path="/superadmin/passports" element={<Protected superAdminOnly><PassportsBrowserPage /></Protected>} />
      {/* NFC check-in pages — require authenticated lodge admin/officer (handled inside the page). */}
      <Route path="/scan" element={<Protected><CheckInScanPage /></Protected>} />
      <Route path="/check-in/:token" element={<Protected><CheckInPage /></Protected>} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/members" element={<MembersPage />} />
        <Route path="/members/new" element={<Protected adminOnly><MemberProfilePage mode="new" /></Protected>} />
        <Route path="/members/:id" element={<MemberProfilePage mode="view" />} />
        <Route path="/global-members" element={<GlobalMembersPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/announcements" element={<Protected adminOnly><AnnouncementsPage /></Protected>} />
        <Route path="/safety-reports" element={<Protected adminOnly><SafetyReportsPage /></Protected>} />
        <Route path="/attendance" element={<AttendancePage />} />
        <Route path="/attendance/:eventId" element={<AttendancePage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/dues" element={<DuesPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/donations" element={<DonationsPage />} />
        <Route path="/transfers" element={<Protected adminOnly><TransfersPage /></Protected>} />
        <Route path="/library" element={<Protected adminOnly><LibraryPage /></Protected>} />
        <Route path="/planse" element={<Protected adminOnly><PlansePage /></Protected>} />
        <Route path="/votes" element={<Protected adminOnly><VotesPage /></Protected>} />
        <Route path="/education" element={<Protected adminOnly><EducationPage /></Protected>} />
        <Route path="/diplomas" element={<Protected adminOnly><DiplomasPage /></Protected>} />
        <Route path="/certificates" element={<Protected adminOnly><CertificatesPage /></Protected>} />
        <Route path="/minutes" element={<Protected adminOnly><MeetingMinutesPage /></Protected>} />
        <Route path="/petitions" element={<PetitionsPage />} />
        <Route path="/recognized-lodges" element={<RecognizedLodgesPage />} />
        <Route path="/chat" element={<Protected adminOnly><LodgeChatPage /></Protected>} />
        <Route path="/mail" element={<Protected adminOnly><MailPage /></Protected>} />
        <Route path="/meetings" element={<Protected adminOnly><OnlineMeetingsPage /></Protected>} />
        <Route path="/lodge-info" element={<LodgeInfoPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/users" element={<Protected adminOnly><UsersPage /></Protected>} />
        <Route path="/audit-log" element={<Protected adminOnly><AuditLogPage /></Protected>} />
        <Route path="/mobile-app" element={<Protected adminOnly><MobileAppPage /></Protected>} />
        <Route path="/jurisdiction-lodges/:lodgeId" element={<Protected adminOnly><JurisdictionLodgeOverviewPage /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <BrowserRouter>
          <MeetingSessionProvider>
            {ADMIN_HOST ? <AdminHostRoutes /> : <MainHostRoutes />}
          </MeetingSessionProvider>
          <InstallPrompt />
          <FrozenGate />
        </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}

/**
 * Listens for the global "lodge-frozen" event (dispatched by the axios
 * interceptor when the backend returns 403 LODGE_FROZEN) and shows a full-screen
 * modal while logging the user out. After the user dismisses the modal we
 * redirect them to /login.
 */
function FrozenGate() {
  const [frozen, setFrozen] = useState(null);
  const { user, logout } = useAuth();

  useEffect(() => {
    const handler = async (ev) => {
      const detail = ev.detail || {};
      setFrozen(detail);
      // Log out the user so the session is cleared.
      if (user && user !== false) {
        try { await logout(); } catch { /* ignore */ }
      }
    };
    window.addEventListener("lodge-frozen", handler);
    return () => window.removeEventListener("lodge-frozen", handler);
  }, [user, logout]);

  const close = () => {
    setFrozen(null);
    try {
      // Hard redirect to /login so any remaining state is reset.
      window.location.replace("/login");
    } catch {
      /* ignore */
    }
  };

  return <FrozenScreen open={frozen} onClose={close} />;
}
